/**
 * Preview stability (docs/materials-perf-plan.md §2.2, PR A1).
 *
 * `previewEventsAtom` used to read the whole `uiAtom`, and `cursorWorldPos` (the exact hit point) is a
 * field of it, so every cursor move produced new preview events, a new composed base, a regroup and a
 * shadow pass even while the pointer stayed inside one cell. Two layers stop that:
 *
 * 1. `selectPreviewInputs` picks out exactly the UI fields `buildPreviewEvents` reads (the hovered cell
 *    and its face, the tool settings, the in-progress gesture state) and `samePreviewInputs` compares
 *    them by value where a move produces fresh objects (the hit) and by reference where only a real
 *    change replaces the object (tool settings, spines, selections). When they match, the atom returns
 *    its previous inputs object, so jotai does not even re-run `buildPreviewEvents`.
 * 2. `samePreviewEvents` is a bounded structural compare of the built events, for inputs that differ
 *    without changing the events (e.g. the eraser's hit `voxel` object, or an empty event list).
 *
 * Equal events give the same composed grid, so returning the previous array lets jotai's `Object.is`
 * check keep `composedStateAtom`, the grouping hook and the shadow policy untouched.
 */
import type { EphemeralState } from '@/uiState'
import type { EventTuple } from '@/events/types'
import type { Hit } from '@/voxelTypes'

// ---------------------------------------------------------------------------------------------
// Narrow selector over the UI state
// ---------------------------------------------------------------------------------------------

/**
 * The UI inputs of `buildPreviewEvents`. `ui` is the snapshot the inputs were taken from: when two
 * snapshots select equal inputs, the preview reads the same values from either, so the older one is
 * kept and handed to `buildPreviewEvents`.
 */
export type PreviewInputs = {
  readonly ui: EphemeralState
  readonly suppressed: boolean
  // Tool settings (replaced by reference on change; cursor moves keep them).
  readonly currentTool: string
  readonly scribble: unknown
  readonly eraserMode: unknown
  readonly paint: unknown
  readonly rectangleFill: boolean
  // Hovered cell and face: the hit is a fresh object per raycast, so these are compared by value.
  // The exact intersection point and distance are deliberately left out.
  readonly hasHit: boolean
  readonly hitX: number
  readonly hitY: number
  readonly hitZ: number
  readonly hitNX: number
  readonly hitNY: number
  readonly hitNZ: number
  readonly hitType: Hit['type'] | null
  readonly hitVoxel: unknown
  readonly hitMirror: boolean
  readonly hitSurface: string | undefined
  // Pointer helpers, read by computeLineEnd only for an axis-constrained (shift) line/spline end.
  readonly clientPos: unknown
  readonly canvasRect: unknown
  readonly shiftDown: boolean
  // Gesture state (replaced by reference when a gesture progresses).
  readonly scribbleSpine: unknown
  readonly paintSpine: unknown
  readonly isDrawing: boolean
  readonly rectangleBounds: unknown
  readonly rectangleInitialNormal: unknown
  readonly shapeState: unknown
  readonly voxelSelection: unknown
  readonly faceSelection: unknown
  readonly activeSubjectRef: unknown
  readonly builderSubject: unknown
  readonly subjectTransforms: unknown
}

export function selectPreviewInputs(ui: EphemeralState, suppressed: boolean): PreviewInputs {
  const tool = ui.tool
  const currentTool = tool.currentTool
  const hit = ui.cursorHit
  const pos = hit?.position
  const n = hit?.normal
  const constrainedLineEnd = ui.shiftDown && (currentTool === 'line' || currentTool === 'spline')
  const u = ui as EphemeralState & { activeSubjectRef?: unknown; builderSubject?: unknown; subjectTransforms?: unknown }
  return {
    ui,
    suppressed,
    currentTool,
    scribble: tool.scribble,
    eraserMode: tool.eraser?.mode,
    paint: tool.paint,
    rectangleFill: !!tool.rectangle?.fillMode,
    hasHit: !!hit,
    hitX: pos ? pos.x : 0,
    hitY: pos ? pos.y : 0,
    hitZ: pos ? pos.z : 0,
    hitNX: n ? n.x : 0,
    hitNY: n ? n.y : 0,
    hitNZ: n ? n.z : 0,
    hitType: hit ? hit.type : null,
    hitVoxel: hit ? hit.voxel : undefined,
    hitMirror: !!hit?.mirror,
    hitSurface: hit?.drawingSurfaceId,
    clientPos: constrainedLineEnd ? ui.clientPos : null,
    canvasRect: constrainedLineEnd ? ui.canvasRect : null,
    shiftDown: !!ui.shiftDown,
    scribbleSpine: ui.scribbleSpine ?? null,
    paintSpine: ui.paintSpine ?? null,
    isDrawing: !!ui.isDrawing,
    rectangleBounds: ui.rectangleBounds,
    rectangleInitialNormal: ui.rectangleInitialNormal,
    shapeState: ui.shapeState,
    voxelSelection: ui.voxelSelection,
    faceSelection: ui.faceSelection,
    activeSubjectRef: u.activeSubjectRef ?? null,
    builderSubject: u.builderSubject ?? null,
    subjectTransforms: u.subjectTransforms,
  }
}

/** Field-wise equality of two selections; `ui` itself is not compared. */
export function samePreviewInputs(a: PreviewInputs, b: PreviewInputs): boolean {
  return a.suppressed === b.suppressed
    && a.currentTool === b.currentTool
    && a.scribble === b.scribble
    && a.eraserMode === b.eraserMode
    && a.paint === b.paint
    && a.rectangleFill === b.rectangleFill
    && a.hasHit === b.hasHit
    && a.hitX === b.hitX && a.hitY === b.hitY && a.hitZ === b.hitZ
    && a.hitNX === b.hitNX && a.hitNY === b.hitNY && a.hitNZ === b.hitNZ
    && a.hitType === b.hitType
    && a.hitVoxel === b.hitVoxel
    && a.hitMirror === b.hitMirror
    && a.hitSurface === b.hitSurface
    && a.clientPos === b.clientPos
    && a.canvasRect === b.canvasRect
    && a.shiftDown === b.shiftDown
    && a.scribbleSpine === b.scribbleSpine
    && a.paintSpine === b.paintSpine
    && a.isDrawing === b.isDrawing
    && a.rectangleBounds === b.rectangleBounds
    && a.rectangleInitialNormal === b.rectangleInitialNormal
    && a.shapeState === b.shapeState
    && a.voxelSelection === b.voxelSelection
    && a.faceSelection === b.faceSelection
    && a.activeSubjectRef === b.activeSubjectRef
    && a.builderSubject === b.builderSubject
    && a.subjectTransforms === b.subjectTransforms
}

// ---------------------------------------------------------------------------------------------
// Structural compare of built events
// ---------------------------------------------------------------------------------------------

/** Nodes visited before `samePreviewEvents` gives up and reports a difference (keeps it O(small)). */
export const PREVIEW_EVENT_COMPARE_BUDGET = 4096

type Vec3Like = { x: number; y: number; z: number; w?: number }

function isVectorLike(v: object): v is Vec3Like {
  return typeof (v as Vec3Like).x === 'number'
    && typeof (v as Vec3Like).y === 'number'
    && typeof (v as Vec3Like).z === 'number'
    && ((v as { isVector3?: boolean }).isVector3 === true || (v as { isVector4?: boolean }).isVector4 === true)
}

function isPlainObject(v: object): boolean {
  const proto = Object.getPrototypeOf(v)
  return proto === Object.prototype || proto === null
}

/**
 * Deep value equality for event payloads: arrays, plain objects, Map, Set and typed arrays are
 * compared by content; three.js vectors by component; any other class instance by reference.
 * Returns false once `budget` nodes have been visited.
 */
export function samePreviewEvents(a: readonly EventTuple[], b: readonly EventTuple[]): boolean {
  if (a === b) return true
  if (a.length !== b.length) return false
  const budget = { left: PREVIEW_EVENT_COMPARE_BUDGET }
  for (let i = 0; i < a.length; i++) {
    if (!deepEqual(a[i], b[i], budget)) return false
  }
  return true
}

function deepEqual(a: unknown, b: unknown, budget: { left: number }): boolean {
  if (Object.is(a, b)) return true
  if (--budget.left < 0) return false
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a)) {
    if (!Array.isArray(b) || a.length !== b.length) return false
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], b[i], budget)) return false
    return true
  }
  if (Array.isArray(b)) return false
  if (a instanceof Map) {
    if (!(b instanceof Map) || a.size !== b.size) return false
    for (const [k, v] of a) {
      if (!b.has(k) || !deepEqual(v, b.get(k), budget)) return false
    }
    return true
  }
  if (b instanceof Map) return false
  if (a instanceof Set) {
    if (!(b instanceof Set) || a.size !== b.size) return false
    for (const v of a) if (!b.has(v)) return false
    return true
  }
  if (b instanceof Set) return false
  if (ArrayBuffer.isView(a)) {
    if (!ArrayBuffer.isView(b) || Object.getPrototypeOf(a) !== Object.getPrototypeOf(b)) return false
    const ta = a as unknown as ArrayLike<number>, tb = b as unknown as ArrayLike<number>
    if (ta.length !== tb.length) return false
    budget.left -= ta.length >> 4
    if (budget.left < 0) return false
    for (let i = 0; i < ta.length; i++) if (ta[i] !== tb[i]) return false
    return true
  }
  if (ArrayBuffer.isView(b)) return false
  if (isVectorLike(a)) {
    return isVectorLike(b) && a.x === b.x && a.y === b.y && a.z === b.z && a.w === b.w
  }
  if (!isPlainObject(a) || !isPlainObject(b)) return false
  const ka = Object.keys(a as object), kb = Object.keys(b as object)
  if (ka.length !== kb.length) return false
  const ra = a as Record<string, unknown>, rb = b as Record<string, unknown>
  for (const k of ka) {
    if (!Object.prototype.hasOwnProperty.call(rb, k)) return false
    if (!deepEqual(ra[k], rb[k], budget)) return false
  }
  return true
}
